import { Box, BoxProps } from "@mui/material";
import React from "react";
import { tabStyleMap, tabContentStyles, tabIndicatorStyles } from "./constants";

export type TabVariant = "primary" | "secondary";

export interface TabProps extends Omit<BoxProps, "variant"> {
  label?: string;
  /** short amber pill after the label (e.g. "New") — Figma's new-feature tab marker */
  badgeLabel?: string;
  icon?: React.ReactNode;
  variant?: TabVariant;
  selected?: boolean;
  hovered?: boolean;
  pressed?: boolean;
  onClick?: () => void;
}

export const Tab: React.FC<TabProps> = ({
  label,
  badgeLabel,
  icon,
  variant = "primary",
  selected = false,
  hovered = false,
  pressed = false,
  onClick,
  ...props
}) => {
  const tabStyles = tabStyleMap[variant]({ selected, hasIcon: !!icon });

  return (
    <Box sx={tabStyles} onClick={onClick} {...props}>
      {variant === "primary" ? (
        <>
          <Box sx={tabContentStyles(variant, !!icon)}>
            {icon && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {icon}
              </Box>
            )}
            {/* the tab content box stacks COLUMN-wise (icon over label) — the label and its badge
                must share one horizontal row or the badge lands on its own line below the text */}
            {(label || badgeLabel) && (
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}
              >
                {label && <Box component="span">{label}</Box>}
                {badgeLabel && (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      px: 0.75,
                      py: 0.1,
                      borderRadius: 999,
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: "16px",
                      color: "#B45309",
                      backgroundColor: "#FEF3C7",
                    }}
                  >
                    {badgeLabel}
                  </Box>
                )}
              </Box>
            )}
          </Box>
          <Box sx={tabIndicatorStyles(variant, selected)} />
        </>
      ) : (
        <>
          {icon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </Box>
          )}
          {label && <Box component="span">{label}</Box>}
          <Box sx={tabIndicatorStyles(variant, selected)} />
        </>
      )}
    </Box>
  );
};

export default Tab;
